import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import useStoreSubdomain from '@/hooks/useStoreSubdomain';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Eye, Clock } from 'lucide-react';

export default function PublicBlog() {
  const { slug } = useParams();
  const subdomain = useStoreSubdomain();
  const [store, setStore] = useState(null);
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const storeRes = await api.get(`/store/${subdomain}`);
        setStore(storeRes.data);
        if (storeRes.data.name) document.title = slug ? `Blog — ${storeRes.data.name}` : `Blog — ${storeRes.data.name}`;
        if (slug) {
          const postRes = await api.get(`/store/${subdomain}/blog/${slug}`);
          setPost(postRes.data);
        } else {
          const res = await api.get(`/store/${subdomain}/blog`);
          setPosts(res.data.posts || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [subdomain, slug]);

  const primaryColor = store?.primary_color || '#2D6CDF';

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  // Single post view
  if (slug && post) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to={`/store/${subdomain}/blog`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft size={20} /><span className="font-medium">Blog</span>
            </Link>
            <Link to={`/store/${subdomain}`}><Button variant="outline" size="sm">{store?.name}</Button></Link>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 py-8">
          {post.featured_image && (
            <img src={post.featured_image} alt={post.title} className="w-full rounded-xl mb-6 max-h-[400px] object-cover" />
          )}
          <h1 className="text-3xl font-bold text-slate-900 mb-3">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
            <span className="flex items-center gap-1"><Calendar size={14} />{new Date(post.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            {post.view_count > 0 && <span className="flex items-center gap-1"><Eye size={14} />{post.view_count} views</span>}
            {post.category && <Badge variant="outline">{post.category}</Badge>}
          </div>
          <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />
        </main>
      </div>
    );
  }

  // Blog list view
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={`/store/${subdomain}`} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} /><span className="font-medium">{store?.name}</span>
          </Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Blog</h1>
        {posts.length === 0 ? (
          <Card className="border-dashed"><CardContent className="py-16 text-center text-slate-400">Belum ada artikel</CardContent></Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map(p => (
              <Link key={p.id || p.slug} to={`/store/${subdomain}/blog/${p.slug}`}>
                <Card className="border-slate-200 overflow-hidden hover:shadow-md transition-shadow h-full">
                  {p.featured_image && (
                    <div className="aspect-video bg-slate-100">
                      <img src={p.featured_image} alt={p.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="p-4">
                    {p.category && <Badge variant="outline" className="text-xs mb-2" style={{ borderColor: primaryColor, color: primaryColor }}>{p.category}</Badge>}
                    <h2 className="font-semibold text-slate-900 line-clamp-2 mb-2">{p.title}</h2>
                    {p.excerpt && <p className="text-sm text-slate-500 line-clamp-3">{p.excerpt}</p>}
                    <p className="text-xs text-slate-400 mt-3 flex items-center gap-1">
                      <Clock size={12} />{new Date(p.created_at).toLocaleDateString('id-ID')}
                    </p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
